<template>
  <section class="featured-publication" :class="{ 'is-embedded': embedded }" :aria-label="entry.title">
    <header class="research-feature-head">
      <div v-if="!embedded" class="research-feature-meta"><span>{{ entry.category }}</span><span class="research-feature-status">{{ entry.status }}</span></div>
      <h3 v-if="!embedded" :id="`${entry.id}-research-title`">{{ entry.title }}</h3>
      <p v-if="!embedded && entry.subtitle" class="research-feature-subtitle">{{ entry.subtitle }}</p>
      <div class="research-feature-byline">
        <p class="research-feature-authors" v-html="authorsHtml"></p>
        <a v-if="entry.lab" class="research-feature-lab" :href="entry.lab.url" target="_blank" rel="noopener">
          <img v-if="entry.lab.logo" :src="entry.lab.logo" :alt="entry.lab.name" />
          <span>Associated with <strong>{{ entry.lab.name }}</strong></span>
        </a>
      </div>
    </header>
    <div class="research-feature-prose">
      <p v-for="(paragraph, index) in paragraphs" :key="index" v-html="paragraph"></p>
    </div>
    <div class="research-feature-resources">
      <component :is="link.internal ? 'router-link' : 'a'" v-for="link in entry.links" :key="link.url" class="resource-action" v-bind="link.internal ? { to: link.url } : { href: link.url, target: '_blank', rel: 'noopener' }">
        <span>{{ link.label }}</span>
        <InterviewIcon v-if="link.icon === 'interview'" />
        <img v-else-if="link.iconSrc" :src="link.iconSrc" alt="" />
        <ArrowUpRightIcon v-else />
      </component>
    </div>
    <FigureSequence :images="entry.images" :title="entry.title" paper />
    <footer class="research-feature-foot"><SourceViewLinks :source-id="entry.sourceId" /></footer>
  </section>
</template>

<script>
import InterviewIcon from './InterviewIcon.vue';
import FigureSequence from './FigureSequence.vue';
import SourceViewLinks from './SourceViewLinks.vue';
import { escapeHtml, escapeRegExp } from '../../utils/highlight';
import '../../assets/css/resource-action.css';

export default {
  name: 'FeaturedPublication',
  components: { InterviewIcon, FigureSequence, SourceViewLinks },
  props: { entry: { type: Object, required: true }, embedded: Boolean },
  computed: {
    paragraphs() { return this.entry.body.split(/<br\s*\/?>(?:\s*<br\s*\/?>)+/i); },
    authorsHtml() {
      const authors = escapeHtml(this.entry.publication.authors || '');
      const self = this.entry.publication.self;
      return self ? authors.replace(new RegExp(escapeRegExp(self), 'g'), `<strong>${escapeHtml(self)}</strong>`) : authors;
    },
  },

};
</script>

<style scoped>
.featured-publication { padding: 38px 0 30px; color: var(--site-text-primary); cursor: var(--site-default-cursor); }
.research-feature-meta { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 24px; font: 11px/1.5 'Quattrocento Sans', sans-serif; letter-spacing: .1em; text-transform: uppercase; color: var(--site-text-tertiary); }
.research-feature-status { color: var(--site-text-secondary); }
.featured-publication h3 { margin: 22px 0 10px; max-width: 900px; font: 700 32px/1.15 'PT Serif', Georgia, serif; letter-spacing: -.025em; }
.research-feature-subtitle { margin: 0; font: 17px/1.5 'PT Serif', Georgia, serif; color: var(--site-text-secondary); }
.research-feature-authors { margin: 16px 0 0; max-width: 900px; font: 13px/1.7 'Quattrocento Sans', sans-serif; color: var(--site-text-tertiary); }
.research-feature-authors :deep(strong) { font-weight: 600; color: var(--site-text-secondary); }
.research-feature-prose { columns: 2 20rem; column-gap: 32px; column-fill: balance; margin-top: 26px; }
.research-feature-prose > p { margin: 0 0 16px; orphans: 3; widows: 3; font: 15px/1.7 'PT Serif', Georgia, serif; color: var(--site-text-secondary); }
.research-feature-prose > p:last-child { margin-bottom: 0; }
.research-feature-resources { display: flex; flex-wrap: wrap; align-items: center; gap: 0 28px; margin: 16px 0 26px; }
.research-feature-resources .resource-action { font-size: 14px; }
.research-feature-resources .resource-action img, .research-feature-resources .resource-action svg { width: 17px; height: 17px; }
.research-feature-byline { display: flex; flex-wrap: wrap; gap: 12px 28px; align-items: center; margin-top: 18px; }
.research-feature-authors { margin: 0; }
.research-feature-lab { display: flex; align-items: center; gap: 12px; color: var(--site-text-secondary); font: 11px/1.5 'Quattrocento Sans', sans-serif; text-decoration: none; }
.research-feature-lab img { width: auto; max-width: 88px; height: 40px; object-fit: contain; }
.research-feature-lab span { display: flex; flex-direction: column; }
.research-feature-lab strong { font-size: 13px; font-weight: 400; }
.featured-publication.is-embedded { padding: 0; }
.is-embedded .research-feature-byline { margin-top: 0; }
.research-feature-foot { margin-top: 20px; }
.research-feature-foot :deep(.source-view-links) { width: 100%; max-width: 480px; margin-top: 0; }
@media (max-width: 800px) {
  .featured-publication h3 { font-size: 30px; }
  .research-feature-prose { column-gap: 24px; }
}
@media (max-width: 600px) {
  .research-feature-foot :deep(.source-view-links) { max-width: 100%; }
  .research-feature-prose { columns: 1; }
  .research-feature-resources { gap: 0 20px; }
  .research-feature-subtitle { font-size: 16px; }
}
</style>
